import { units } from "./units";
import { buildings } from "./buildings";
import { weapons, damageTable } from "./weapons";
import { upgrades } from "./upgrades";

export const pace = {
  construction: 0.5,
  training: 0.5,
  firstAttack: 0.5,
};

export const rules = {
  dronePads: 5,
  droneLaunchSpacing: 6,
  airfieldSlots: 4,
  // Seconds a homeless aircraft stays in the air before it crashes.
  strandedFuel: 60,
  builderRadius: 480,
  supplyRadius: 640,
  missileCost: 2000,
  missileTime: 120,
  aftermathTime: 5,
  engineerCaptureTicks: 180,
  oilDerrickIncomeRate: 6,
  // Share of building passive income paid under low power. Derricks are unaffected.
  lowPowerIncome: 0.25,
  // Army upkeep: army pop (excludes builders and haulers) above a threshold cuts income.
  // Tier 1 starts above upkeepLowPop. Tier 2 starts above upkeepHighPop.
  upkeepLowPop: 100,
  upkeepHighPop: 150,
  upkeepLowIncome: 0.8,
  upkeepHighIncome: 0.4,
  oilDerrickScaling: 0.25,
  oilDerrickCooldownTicks: 600,
  depotServiceRange: 128,
  depotServiceRate: 20,
  objectiveRepairPrice: 200,
  cratePayout: 1000,
  cratePickupRadius: 16,
  missileSpeed: 300,
  missileEdgeDamage: 0.25,
  startMoney: 5000,
  matchDuration: 1200,
  supplies: 30000,
  firstAttack: 240,
  attackSize: 8,
  sellFraction: 0.5,
  repairCost: 0.2,
  repairRate: 25,
  lowPowerRate: 0.25,
  // Kill loot: share of a destroyed enemy's cost paid to the killer owner.
  // Minimal by design; dominance scaling reduces this further.
  lootFraction: 0.01,
  // Anti-snowball: loot shrinks while the killer's army outvalues the victim
  // owner's (scaled by their ratio, never below this floor). 1 disables it.
  lootDominanceFloor: 0.25,
  constructionInitialHealth: 0.1,
  recoveryMoney: 4500,
  recoveryHoldTicks: 900,
  catchUpThreshold: 0.85,
  catchUpHoldTicks: 900,
  catchUpDurationTicks: 5400,
  catchUpCooldownTicks: 1800,
  catchUpDockMultiplier: 1.5,
  catchUpProductionMultiplier: 1.5,
  // F02 dock replenishment: supplies regrow per dock at this sim-time rate
  // (supplies/second) while below dockReplenishCeiling. Slow by design.
  dockReplenishRate: 60,
  dockReplenishCeiling: 8000,
  // Anti-snowball dock saturation: an owner's first dockSlots trucks on one
  // dock load full cargo; each extra truck adds only dockOverflowYield of a
  // load to that dock's throughput. More income means holding more docks.
  // A yield of 1 disables saturation.
  dockSlots: 3,
  dockOverflowYield: 0.25,
  // Mobile units near their own completed headquarters receive home protection.
  homeDefenseRadius: 720,
  homeDamageTaken: 0.4,
  // Veterancy: each rank costs this multiple of the unit's own cost in
  // destroyed enemy value. Every rank adds veterancyDamage to damage dealt and
  // removes veterancyDamageTaken from damage taken. Heroic (rank 3) units
  // regenerate heroicHealRate of their maximum health per second.
  veterancyThreshold: 3,
  veterancyDamage: 0.1,
  veterancyDamageTaken: 0.1,
  heroicHealRate: 0.02,
};

export const difficulties = {
  easy: {
    firstAttack: 360,
    attackSize: 5,
    interval: 120,
    armyLimit: 16,
    buildRate: 0.85,
  },
  normal: {
    firstAttack: 270,
    attackSize: 10,
    interval: 60,
    armyLimit: 28,
    buildRate: 1,
  },
  hard: {
    firstAttack: 210,
    attackSize: 16,
    interval: 45,
    armyLimit: 40,
    buildRate: 1.2,
  },
};

// Tunable literals live here. Identifiers, asset names, prerequisites, and
// supported mechanics remain code invariants, outside the editor allowlist.
export const score = {
  bankWeight: 1,
  unitWeight: 1,
  buildingWeight: 1,
  killWeight: 0.5,
};

export const presentation = {
  distanceHaze: 0.000035,
  fundsEaseMs: 400,
  minimapUnseenOpacity: 1,
  minimapExploredOpacity: 0.2,
};

export const ai = {
  supplyLowFraction: 0.4,
  gathererReserve: 600,
  depotReserve: 1500,
  powerReserve: 800,
  gatherers: 3,
  lowSupplyGatherers: 5,
  hardDefenders: 2,
  normalDefenders: 4,
  easyDefenders: 6,
  defenseRadius: 400,
  defenseOvermatch: 1.5,
  guardRadius: 500,
  // Each observed guard adds assault units.
  guardWaveBonus: 4,
  guardLimit: 3,
};

export const gameplayDefaults = {
  upgrades,
  units,
  buildings,
  weapons,
  damageTable,
  rules,
  difficulties,
  pace,
  score,
  presentation,
  ai,
};

/** Editor limits. Conditional bounds stay beside the settings resolver. */
export const settingsBounds = {
  enabled: { type: "integer", unit: "toggle", min: 0, max: 1 },
  engineerCaptureTicks: { type: "integer", unit: "ticks", min: 1, max: 3600 },
  objectiveRadius: { type: "number", unit: "world units", min: 0, max: 8192 },
  oilDerrickCooldownTicks: {
    type: "integer",
    unit: "ticks",
    min: 0,
    max: 216000,
  },
  cratePayout: { type: "number", unit: "credits", min: 0, max: 1000000 },
  oilDerrickIncomeRate: {
    type: "number",
    unit: "per second",
    min: 0,
    max: 10000,
  },
  oilDerrickScaling: { type: "number", unit: "coefficient", min: 0, max: 10 },
  lowPowerIncome: { type: "number", unit: "multiplier", min: 0, max: 1 },
  upkeepLowPop: { type: "integer", unit: "population", min: 0, max: 1000 },
  upkeepLowIncome: { type: "number", unit: "multiplier", min: 0, max: 1 },
  footprint: { type: "integer", unit: "tiles", min: 1, max: 8 },
  recoveryHoldTicks: { type: "integer", unit: "ticks", min: 1, max: 216000 },
  airfieldSlots: { type: "integer", unit: "aircraft", min: 1, max: 150 },
  limit: { type: "integer", unit: "units", min: 1, max: 150 },
  income: { type: "number", unit: "credits/s", min: 0, max: 10000 },
  rate: { type: "number", unit: "health/s", min: 0, max: 10000 },
  fundsEaseMs: { type: "number", unit: "milliseconds", min: 1, max: 2000 },
  airDamageMultiplier: { type: "number", unit: "multiplier", min: 0, max: 10 },
  deploy: { type: "number" },
  range: { type: "number", unit: "world units", min: 32, max: 8192 },
  constructionInitialHealth: {
    type: "number",
    unit: "fraction",
    min: 0.01,
    max: 1,
  },
  power: { type: "integer", unit: "power", min: -100, max: 100 },
  credits: { type: "integer", unit: "credits", max: 1_000_000 },
  unitCount: { type: "integer", unit: "units", max: 150 },
  distance: { type: "number", unit: "world units", max: 8192 },
  speed: { type: "number", unit: "world units/s", min: 1, max: 2000 },
  interval: { type: "integer", unit: "ticks", min: 1, max: 3600 },
  seconds: { type: "number", unit: "seconds", min: 0.05, max: 3600 },
  health: { type: "number", min: 1, max: 100_000 },
  distanceHaze: { type: "number", unit: "density", min: 0, max: 0.001 },
  fraction: { type: "number", unit: "fraction", max: 1 },
  dockReplenishRate: { type: "number", unit: "credits/s", min: 0, max: 10000 },
  dockSlots: { type: "integer", unit: "trucks", min: 1, max: 150 },
  pop: { type: "integer", unit: "population", min: 1, max: 10 },
  multiplier: { type: "number", unit: "multiplier", max: 10 },
} as const;
